import * as Sentry from '@sentry/browser';
import debug from 'debug';
import mitt, { type Emitter } from 'mitt';

import { App } from 'config/setup';
import { DebugLogsNamespace } from 'constants/debug-logs-namespace';
import promiseBackoff, { BackoffError, type BackoffStatus, STOP_RETRYING_ERROR_MESSAGE } from 'helpers/promise-backoff';
import { getWebsocketConnection } from 'services/server/websocket-connection';

import { connectivityStatusClient } from '../status/client';
import { ConnectivityStatusEvent, type ConnectivityStatusInstance } from '../status/types';

import {
  type Reconnection,
  type ReconnectionDetails,
  ReconnectionStatus,
  ReconnectorEvent,
  type ReconnectorEventMap,
} from './types';

const RECONNECTION_SUCCEEDED_STATUS_LENGTH = 2500;

const RECONNECT_TIMEOUT = 6 * 60 * 1000; // 6 minutes

const log = debug(DebugLogsNamespace.AppLc3PlatformProtocolParser);

/**
 * Represents a reconnection service that handles the reconnection process
 * for a WebSocket connection.
 */
class Reconnector {
  private connectivityStatus: ConnectivityStatusInstance;
  private reconnection: Reconnection | null = null;
  private shouldStopReconnecting = false;
  private reconnectionDetails: ReconnectionDetails = {
    status: ReconnectionStatus.Idle,
    nextReconnectAttemptTimestamp: null,
  };

  private emitter: Emitter<ReconnectorEventMap>;

  /**
   * Creates an instance of Reconnector.
   */
  constructor() {
    this.connectivityStatus = connectivityStatusClient;
    void this.connectivityStatus.initialize();
    this.emitter = mitt();
  }

  /**
   * Retrieves the reconnection details.
   * @returns The reconnection details.
   */
  public getReconnectionDetails(): ReconnectionDetails {
    return this.reconnectionDetails;
  }

  /**
   * Sets the reconnection details.
   * @param details - The reconnection details.
   */
  public setReconnectionDetails(details: ReconnectionDetails): void {
    this.reconnectionDetails = details;
    this.emitter.emit(ReconnectorEvent.RECONNECTION_DETAILS_CHANGED, details);
    log(`Reconnection details updated: ${JSON.stringify(details)}`);
  }

  /**
   * Gets the online status of the service.
   * @returns {boolean} The online status of the service.
   */
  public get isOnline(): boolean {
    return !this.connectivityStatus.getThrottledIsOffline();
  }

  public get isReconnecting(): boolean {
    return [ReconnectionStatus.Retrying, ReconnectionStatus.Scheduled].includes(this.reconnectionDetails.status);
  }

  /**
   * Subscribes to the online status change event.
   *
   * @param handler - The callback function to be called when the online status changes.
   */
  public subscribeToOnlineChange(handler: (isOnline: boolean) => void): void {
    return this.connectivityStatus.subscribe(ConnectivityStatusEvent.ONLINE_STATUS_CHANGED, handler);
  }

  /**
   * Subscribes to reconnection details change events.
   * @param handler - The callback function to be called when reconnection details change.
   */
  public subscribeToReconnectionDetailsChange(handler: (details: ReconnectionDetails) => void): void {
    this.emitter.on(ReconnectorEvent.RECONNECTION_DETAILS_CHANGED, handler);
  }

  /**
   * Unsubscribes from reconnection details change events.
   * @param handler - The callback function to be removed.
   */
  public unsubscribeFromReconnectionDetailsChange(handler: (details: ReconnectionDetails) => void): void {
    this.emitter.off(ReconnectorEvent.RECONNECTION_DETAILS_CHANGED, handler);
  }

  /**
   * Reconnects the service using the provided reconnection options.
   * Reconnection process can be stopped (see "stop" method) due to "offline" browser event.
   * That's why we use "onFail" callback instead of classic "Promise.reject" approach.
   *
   * @param reconnection - The reconnection options.
   * @returns A Promise that resolves when the reconnection is successful, or rejects with an error if the reconnection fails.
   */
  public async reconnect(reconnection: Reconnection): Promise<void> {
    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'reconnect',
      message: `Reconnecting because of: ${reconnection.reason}...`,
      data: {
        alreadyReconnecting: this.isReconnecting,
        currentReason: this.reconnection?.reason,
      },
    });

    if (this.isReconnecting) {
      log('Reconnection already in progress');

      return;
    }

    // Based on our investigation, the reconnection can be triggered twice in short time.
    // Once for `socket_disconnected` and once for `agent_disconnected`.
    // It looks like the `retrying` or `scheduled` status is applied too late and we end up
    // with two reconnection processes.
    // To avoid this, we set the status to `scheduled` manually.
    this.updateReconnectionDetails(ReconnectionStatus.Scheduled, null);

    const { reason, maxAttempts = 1, initialDelay = 0, onFail } = reconnection;
    this.reconnection = reconnection;

    log(`Reconnecting because of: ${reason}...`);

    try {
      await this.performReconnectionWithBackoff(maxAttempts, initialDelay);
      log('Reconnection with backoff successful');
      this.handleSuccessfulReconnection(reason);
    } catch (error) {
      log('Reconnection with backoff failed');
      this.handleFailedReconnection(error, reason, onFail);
    }
  }

  /**
   * Restarts the reconnection process.
   * If there is no stored reconnection, it logs a message and returns.
   * Otherwise, it sets the `shouldStopReconnecting` flag to false,
   * and initiates the reconnection process with the stored reconnection.
   */
  public restart(): void {
    if (!this.reconnection) {
      log('No stored reconnection to restart');

      return;
    }
    this.shouldStopReconnecting = false;

    void this.reconnect(this.reconnection);

    return;
  }

  /**
   * Stops the reconnection process if it is currently in progress.
   */
  public stop(): void {
    if (this.reconnectionDetails.status === ReconnectionStatus.Idle) {
      log('No reconnection in progress to stop');

      return;
    }

    this.setReconnectionDetails({
      status: ReconnectionStatus.Idle,
      nextReconnectAttemptTimestamp: null,
    });

    this.shouldStopReconnecting = true;
  }

  /**
   * Gets a value indicating whether the reconnector is currently handling reconnection.
   * The reconnector is considered to be handling reconnection if it is not in the 'idle' state.
   * Note that the reconnector can still be handling reconnection even if the 'offline' event was fired before
   * the browser went 'online'.
   * @returns A boolean value indicating whether the reconnector is handling reconnection.
   */
  public get isHandlingReconnection(): boolean {
    return !!this.reconnection;
  }

  public loginSuccessful(): void {
    log('Emitting LOGIN_SUCCESSFUL event');
    this.emitter.emit(ReconnectorEvent.LOGIN_SUCCESSFUL);
  }

  public loginFailed(): void {
    log('Emitting LOGIN_FAILED event');
    this.emitter.emit(ReconnectorEvent.LOGIN_FAILED);
  }

  /**
   * Performs the reconnection process with backoff.
   *
   * @param maxAttempts - Maximum number of reconnection attempts.
   */
  private async performReconnectionWithBackoff(maxAttempts: number, initialDelay: number): Promise<void> {
    await promiseBackoff(
      () => this.closeAndLogin(),
      maxAttempts,
      initialDelay,
      () => this.shouldStopReconnecting,
      {
        onRetryScheduled: this.updateReconnectionDetails.bind(this),
        onRetry: this.updateReconnectionDetails.bind(this),
      },
    );
  }

  /**
   * Closes the server and logs in to the WebSocket.
   *
   * @returns A Promise that resolves if login is successful, otherwise rejects.
   */
  private closeAndLogin(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isOnline) {
        log('We are offline and cannot reconnect');
        reject(new Error('We are offline and cannot reconnect'));

        return;
      }

      const timeout = setTimeout(() => {
        // It may happen in sleep mode when we don't have internet connection
        log('Reconnection timeout reached');
        const error = new Error('Reconnection timeout reached');
        Sentry.addBreadcrumb({
          type: 'error',
          level: 'error',
          category: 'reconnect',
          message: `Reconnection timeout reached`,
          data: {
            currentReason: this.reconnection?.reason,
            isOnline: this.isOnline,
            reconnectionStatus: this.reconnectionDetails.status,
          },
        });
        removeEventHandlers();
        reject(error);
      }, RECONNECT_TIMEOUT);

      const removeEventHandlers = (): void => {
        this.emitter.off(ReconnectorEvent.LOGIN_SUCCESSFUL, loginSuccessfulHandler);
        this.emitter.off(ReconnectorEvent.LOGIN_FAILED, loginFailedHandler);
        clearTimeout(timeout);
      };

      const loginSuccessfulHandler = (): void => {
        log('Removing event handlers and resolving promise');
        removeEventHandlers();
        resolve();
      };

      const loginFailedHandler = (): void => {
        log('Removing event handlers and rejecting promise');
        removeEventHandlers();
        reject(new Error('Login with cookies failed'));
      };

      this.emitter.on(ReconnectorEvent.LOGIN_SUCCESSFUL, loginSuccessfulHandler);
      this.emitter.on(ReconnectorEvent.LOGIN_FAILED, loginFailedHandler);

      log('Closing websocket and logging in again');
      getWebsocketConnection().disconnect();
      void App.loginWithCookies?.();
    });
  }

  /**
   * Handles successful reconnection.
   *
   * @param reason - Reason for reconnection.
   */
  private handleSuccessfulReconnection(reason: string): void {
    log(`Successful reconnection (reason: ${reason})`);
    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'reconnect',
      message: `Reconnected successfully (reason: ${reason})`,
    });

    this.setReconnectionDetails({
      nextReconnectAttemptTimestamp: null,
      status: ReconnectionStatus.Succeeded,
    });
    this.reconnection = null;
    this.shouldStopReconnecting = false;

    setTimeout(() => {
      if (this.reconnectionDetails.status === ReconnectionStatus.Succeeded) {
        this.setReconnectionDetails({
          nextReconnectAttemptTimestamp: null,
          status: ReconnectionStatus.Idle,
        });
      }
    }, RECONNECTION_SUCCEEDED_STATUS_LENGTH);
  }

  /**
   * Handles failed reconnection.
   *
   * @param error - Error that occurred during reconnection.
   * @param reason - Reason for reconnection.
   * @param onFail - Optional callback to handle failure.
   */
  private handleFailedReconnection(error: unknown, reason: string, onFail?: (error: unknown) => void): void {
    this.shouldStopReconnecting = false;

    if ((error as Error)?.message === STOP_RETRYING_ERROR_MESSAGE) {
      return;
    }

    const errorDetails = error instanceof BackoffError ? error.backoffErrors.toString() : (error as Error).message;
    log(`Could not reconnect on '${reason}' - ${errorDetails}`);

    Sentry.addBreadcrumb({
      type: 'error',
      level: 'error',
      category: 'reconnect',
      message: `Failed to reconnect. Reason - ${reason}, details - ${errorDetails}`,
      data: {
        errorDetails,
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : JSON.stringify(error),
      },
    });

    if (onFail) {
      this.reconnection = null;
    }

    this.updateReconnectionDetails(ReconnectionStatus.Failed, null, !onFail);
    onFail?.(error);
  }

  /**
   * Updates the reconnection details.
   *
   * @param status - The status of the reconnection.
   * @param nextReconnectAttemptTimestamp - The timestamp of the next reconnect attempt.
   * @param allowManual - Indicates whether manual reconnection is allowed. Default is true.
   */
  private updateReconnectionDetails(
    status: ReconnectionDetails['status'] | BackoffStatus,
    nextReconnectAttemptTimestamp: ReconnectionDetails['nextReconnectAttemptTimestamp'],
    allowManual = true,
  ): void {
    this.setReconnectionDetails({
      status: status as ReconnectionStatus,
      nextReconnectAttemptTimestamp,
      allowManual,
    });
  }
}

let reconnector: Reconnector | null = null;

export function getReconnector(): Reconnector {
  if (!reconnector) {
    reconnector = new Reconnector();
  }

  return reconnector;
}
